// src/api/auth.api.js
import api, { tokenManager, logoutUser } from './axiosClient';

export const authAPI = {
  async register(data) {
    const response = await api.post('/auth/register', {
      email: data.email,
      password: data.password,
      full_name: data.fullName,
      phone: data.phone ?? undefined,
      marketing_consent: data.marketingConsent ?? false,
      referral_code: data.referralCode ?? undefined,
      utm_source: data.utmSource ?? undefined,
      utm_medium: data.utmMedium ?? undefined,
      utm_campaign: data.utmCampaign ?? undefined,
    });
    // Do NOT store tokens after registration.
    // User must log in manually so the OTP flow (FIRST_LOGIN) is triggered.
    // Storing the token here would allow checkAuth() to auto-login the user
    // on the next render, bypassing the OTP gate entirely.
    return response.data;
  },

  async login(data) {
    const response = await api.post('/auth/login', {
      email: data.email,
      password: data.password,
      device_fingerprint: data.deviceFingerprint,
    });

    const { access_token, expires_in, requires_2fa } = response.data;

    // If OTP is required, do NOT store tokens yet — return the flag to the caller
    if (requires_2fa) {
      return response.data; // { requires_2fa: true, otp_reason: "...", user_id: "..." }
    }

    // refresh_token is null in login body — it's in the HttpOnly cookie.
    // Pass null; setTokens() will skip storing it.
    tokenManager.setTokens(access_token, null, expires_in);
    return response.data;
  },

  /**
   * Resend the step-up OTP for an in-progress login.
   * POST /auth/otp/resend
   * Returns { message, cooldown_remaining } — or throws on 429/500.
   */
  async resendOTP(email) {
    const response = await api.post('/auth/otp/resend', { email });
    return response.data;
  },

  /**
   * Complete step-up login after the user enters their OTP.
   * POST /auth/verify-otp
   */
  async verifyLoginOTP({ email, otpCode, deviceId }) {
    const response = await api.post('/auth/verify-otp', {
      email,
      otp_code: otpCode,
      device_id: deviceId,
    });

    const { access_token, expires_in } = response.data;
    // OTP login sets refresh_token as HttpOnly cookie only — not in body
    tokenManager.setTokens(access_token, null, expires_in);
    return response.data;
  },

  async getCurrentUser() {
    const response = await api.get('/auth/me');
    return response.data;
  },

  async logout() {
    await logoutUser();
  },

  async changePassword(currentPassword, newPassword) {
    await api.post('/auth/change-password', {
      current_password: currentPassword,
      new_password: newPassword,
    });
    // Do NOT clear tokens here. The existing access token remains valid after
    // a password change — the backend only invalidates tokens when
    // logout_all_devices=true is passed. Clearing tokens here silently
    // unauthenticates the user mid-session: they stay on the Account page
    // but every subsequent authenticated request (e.g. saving an address)
    // returns 401 because the Bearer header is now empty.
    //
    // If you want to force re-login after a password change, dispatch
    // auth:logout and redirect to /login explicitly — don't clear tokens
    // in the background while the user is still navigating the page.
  },

  // ── OTP-gated password reset (3-step flow) ─────────────────────────────────

  /**
   * Step 1 — Request a password-reset OTP.
   * POST /auth/forgot-password
   * Always returns { message } — backend never reveals if email exists.
   */
  async forgotPassword(email) {
    const response = await api.post('/auth/forgot-password', { email });
    return response.data;
  },

  /**
   * Step 1b — Resend the password-reset OTP (30-second cooldown enforced).
   * POST /auth/forgot-password/resend
   * Returns { message, cooldown_remaining } or throws 429 with detail.
   */
  async resendResetOTP(email) {
    const response = await api.post('/auth/forgot-password/resend', { email });
    return response.data;
  },

  /**
   * Step 2 — Verify the 6-digit OTP.
   * POST /auth/verify-reset-otp
   * Returns { reset_token } — a short-lived JWT for Step 3.
   * Throws 401 on invalid/expired/locked OTP.
   */
  async verifyResetOTP({ email, otp }) {
    const response = await api.post('/auth/verify-reset-otp', { email, otp });
    return response.data; // { reset_token, message }
  },

  /**
   * Step 3 — Set a new password using the verified reset_token.
   * POST /auth/reset-password
   * Returns { message } on success.
   * Throws 401 if token is invalid/expired/already used.
   * Throws 422 if password fails strength requirements.
   *
   * NOTE: reset_token is passed in the request body — it is NEVER placed in
   * the URL to prevent it appearing in server logs, Referer headers, or
   * browser history.
   */
  async resetPassword({ email, reset_token, new_password }) {
    const response = await api.post('/auth/reset-password', {
      email,
      reset_token,
      new_password,
    });
    return response.data;
  },

  // ── Kept for backward compatibility (old link-based flow stubs) ─────────────
  // These are no longer called by the UI but may be referenced in tests.

  async requestPasswordReset(email) {
    return this.forgotPassword(email);
  },

  async confirmPasswordReset(token, newPassword) {
    // Old link-based endpoint — kept for compatibility; not used by new UI
    await api.post('/auth/password-reset/confirm', {
      token,
      new_password: newPassword,
    });
  },

  // ── Email verification ──────────────────────────────────────────────────────

  async verifyEmail(token) {
    const response = await api.post('/auth/verify-email', { token });
    return response.data;
  },

  async resendVerificationEmail(email) {
    const response = await api.post('/auth/verify-email/resend', { email });
    return response.data;
  },

  // ── Profile & addresses ─────────────────────────────────────────────────────

  async updateProfile(data) {
    const response = await api.put('/auth/me', data);
    return response.data;
  },

  async getAddresses() {
    const response = await api.get('/auth/addresses');
    return response.data;
  },

  async addAddress(addressData) {
    const response = await api.post('/auth/addresses', addressData);
    return response.data;
  },

  async updateAddress(addressId, addressData) {
    const response = await api.put(`/auth/addresses/${addressId}`, addressData);
    return response.data;
  },

  async deleteAddress(addressId) {
    await api.delete(`/auth/addresses/${addressId}`);
  },

  // ── 2FA ────────────────────────────────────────────────────────────────────

  async enable2fa(password) {
    const response = await api.post('/auth/2fa/enable', { password });
    return response.data;
  },

  async verify2fa(code) {
    const response = await api.post('/auth/2fa/verify', { code });
    return response.data;
  },

  async disable2fa(password, code) {
    const response = await api.post('/auth/2fa/disable', { password, code });
    return response.data;
  },

  // ── Sessions ────────────────────────────────────────────────────────────────

  async getSessions() {
    const response = await api.get('/auth/sessions');
    return response.data;
  },

  async revokeSession(sessionId) {
    await api.delete(`/auth/sessions/${sessionId}`);
  },

  async logoutAllDevices() {
    await api.post('/auth/logout/all');
  },
};